import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BookOpen, Bot, Camera, Check, Dice5, MessageCircleQuestion, Package, PackageSearch, RotateCcw, Search, Send,
  ShieldCheck, Sparkles, ThumbsDown, ThumbsUp, Truck, UserRound, Wand2, X,
} from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import ReplaiMark from '../components/ReplaiMark';
import { timeAgo } from '../components/bible/rich';
import V2Shell from './V2Shell';

const API = 'api/replai/v2/simulations/';

const SCENARIOS = [
  { emoji: '📦', label: "Where's my order?", text: 'Hi, I ordered a lamp a few weeks ago and still have no news. When will it arrive?' },
  { emoji: '✋', label: 'Cancel please', text: "Hello, I'd like to cancel my order please, I found it cheaper elsewhere." },
  { emoji: '💥', label: 'Arrived broken', text: 'The lamp arrived today but the glass shade is broken. What now?' },
  { emoji: '↩️', label: 'Return', text: "I received my order but I don't like the colour. How can I return it?" },
  { emoji: '📸', label: 'Live photos?', text: 'Do you have real-life photos of this lamp? I want to see how it looks before ordering.' },
  { emoji: '🇺🇸', label: 'Ship to the US', text: 'Can you ship this lamp to New York? Will it work on 110V?' },
  { emoji: '🧾', label: 'Invoice', text: 'Could you send me the invoice for my order with our company VAT number?' },
];

const PERSONAS = [
  { name: 'Matteo Rossi', language: '' }, { name: 'Anneke de Vries', language: 'nl' },
  { name: 'Camille Laurent', language: 'fr' }, { name: 'Olivia Smith', language: 'en' },
  { name: 'Lukas Weber', language: '' }, { name: 'Sofia García', language: '' },
];

const LANGS = [['', 'Auto-detect'], ['en', 'English'], ['nl', 'Dutch'], ['fr', 'French'], ['de', 'German'], ['it', 'Italian']];

const stepIcon = (text) => {
  const t = text.toLowerCase();
  if (t.includes('bible')) return BookOpen;
  if (t.includes('claude') || t.includes('ferry')) return Sparkles;
  if (t.includes('carrier') || t.includes('shipment') || t.includes('shipping')) return Truck;
  if (t.includes('order')) return Package;
  if (t.includes('photo')) return Camera;
  if (t.includes('catalogue') || t.includes('product') || t.includes('brand') || t.includes('manual')) return PackageSearch;
  if (t.includes('rejected')) return RotateCcw;
  if (t.includes('layout') || t.includes('check')) return ShieldCheck;
  return Search;
};

// [label](url) -> link, everything else as text, line breaks kept.
const RichText = ({ text }) => (
  <div className="whitespace-pre-wrap leading-relaxed">
    {(text || '').split(/(\[[^\]]+\]\(https?:\/\/[^)\s]+\))/g).map((part, i) => {
      const m = part.match(/^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)$/);
      return m
        ? <a key={i} href={m[2]} target="_blank" rel="noreferrer" className="font-medium text-[var(--pg)] underline underline-offset-2">{m[1]}</a>
        : <React.Fragment key={i}>{part}</React.Fragment>;
    })}
  </div>
);

const Avatar = ({ who, name }) => (who === 'replai'
  ? <div className="pg-avatar pg-avatar-replai"><ReplaiMark size={22} /></div>
  : <div className="pg-avatar pg-avatar-customer" aria-hidden>{(name || '?').trim().charAt(0).toUpperCase() || <UserRound size={16} />}</div>);

const Thinking = ({ progress, since }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 500); return () => clearInterval(t); }, []);
  const secs = Math.max(0, Math.round((now - since) / 1000));
  return (
    <div className="pg-card pg-thinking">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 font-semibold">
          <span className="pg-dots" aria-hidden><i /><i /><i /></span> Replai is working on it
        </div>
        <span className="num rounded-full bg-[var(--pg-tint)] px-2.5 py-0.5 text-sm font-semibold text-[var(--pg)]">{secs}s</span>
      </div>
      <p className="mt-1 text-sm text-[var(--ink-3)]">Same Bible, same data, same checks as a real ticket. Usually 30 to 90 seconds.</p>
      <ol className="mt-3 space-y-1.5" aria-live="polite">
        {(progress || []).map((p, i) => {
          const Icon = stepIcon(p.text);
          const last = i === progress.length - 1;
          return (
            <li key={`${i}-${p.text}`} className={`pg-step ${last ? 'pg-step-live' : ''}`}>
              <span className="pg-step-icon"><Icon size={14} strokeWidth={2} /></span>
              <span className="min-w-0 flex-1">{p.text}</span>
              <span className="num shrink-0 text-xs text-[var(--ink-3)]">{last ? <span className="pg-spin" /> : <Check size={13} />}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

const Rate = ({ sim, index, onSaved }) => {
  const fb = (sim.feedback || {})[String(index)];
  const [note, setNote] = useState('');
  const [pick, setPick] = useState(null);
  const save = (verdict) => axiosInstance.post(`${API}${sim.id}/`, { turn: index, verdict, note })
    .then(({ data }) => { onSaved(data); setPick(null); });
  if (fb) {
    return (
      <div className="mt-3 flex items-center gap-2 text-sm text-[var(--ink-2)]">
        {fb.verdict === 'correct' ? <ThumbsUp size={15} className="text-green-700" /> : <ThumbsDown size={15} className="text-red-700" />}
        {fb.by ? `${fb.by}: ` : ''}{fb.note || (fb.verdict === 'correct' ? 'good answer' : 'not right')}
      </div>
    );
  }
  return (
    <div className="mt-3 border-t border-[var(--line-2)] pt-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-[var(--ink-3)]">Would you send this?</span>
        <button type="button" onClick={() => setPick('correct')} className={`pg-chip ${pick === 'correct' ? 'pg-chip-on' : ''}`}><ThumbsUp size={14} /> Yes</button>
        <button type="button" onClick={() => setPick('incorrect')} className={`pg-chip ${pick === 'incorrect' ? 'pg-chip-on' : ''}`}><ThumbsDown size={14} /> Not like this</button>
      </div>
      {pick && (
        <div className="mt-2 flex gap-2">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={pick === 'correct' ? 'Optional comment' : 'What should be different?'}
            className="pg-input flex-1" onKeyDown={(e) => e.key === 'Enter' && save(pick)} />
          <button type="button" onClick={() => save(pick)} className="pg-btn">Save</button>
        </div>
      )}
    </div>
  );
};

const Answer = ({ turn, sim, index, onSaved }) => {
  const r = turn.result || {};
  const agent = r.audience === 'agent';
  const conf = Math.round((r.confidence || 0) * 100);
  return (
    <div className="pg-card">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`pg-badge ${agent ? 'pg-badge-agent' : 'pg-badge-ready'}`}>
          {agent ? 'Needs you' : 'Reply to the customer'}
        </span>
        {r.confidence != null && (
          <span className="flex items-center gap-1.5 text-xs text-[var(--ink-3)]">
            <span className="pg-meter"><span style={{ width: `${conf}%` }} /></span> {conf}% sure
          </span>
        )}
      </div>
      {agent ? (
        <div className="mt-3 space-y-3 text-[0.95rem]">
          <div><div className="pg-label">What's going on</div><RichText text={r.summary || r.reply} /></div>
          {(r.actions || []).length > 0 && (
            <div><div className="pg-label">What to do</div>
              <ul className="list-disc space-y-0.5 pl-5">{r.actions.map((a) => <li key={a}>{a}</li>)}</ul></div>
          )}
          <div><div className="pg-label">Suggested reply</div>
            {r.suggested_reply ? <div className="pg-quote"><RichText text={r.suggested_reply} /></div>
              : <p className="text-[var(--ink-3)]">None: a colleague writes it once they know.</p>}</div>
        </div>
      ) : <div className="mt-3 text-[0.97rem]"><RichText text={r.reply} /></div>}
      <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[var(--ink-3)]">
        {(r.bible_blocks || []).length > 0 && <span className="flex items-center gap-1"><BookOpen size={12} /> Bible {r.bible_blocks.join(', ')}</span>}
        <span>{(turn.tools || []).length} lookups</span>
        <span>{turn.ferry_calls || 0} Ferry calls</span>
        {turn.secs != null && <span>{turn.secs}s</span>}
      </div>
      {(turn.progress || []).length > 0 && (
        <details className="mt-2 text-sm">
          <summary className="cursor-pointer text-[var(--ink-2)] hover:text-[var(--ink)]">How Replai got here ({turn.progress.length} steps)</summary>
          <ol className="mt-2 space-y-1">{turn.progress.map((p, i) => {
            const Icon = stepIcon(p.text);
            return <li key={i} className="flex items-center gap-2 text-[var(--ink-2)]"><Icon size={13} className="shrink-0 text-[var(--pg)]" /><span className="num w-10 shrink-0 text-xs text-[var(--ink-3)]">{p.at}s</span>{p.text}</li>;
          })}</ol>
        </details>
      )}
      <Rate sim={sim} index={index} onSaved={onSaved} />
    </div>
  );
};

const SimulateV2 = () => {
  const [persona, setPersona] = useState({ name: 'Matteo Rossi', email: '', order_number: '', language: '' });
  const [text, setText] = useState('');
  const [sim, setSim] = useState(null);
  const [recent, setRecent] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [since, setSince] = useState(Date.now());
  const endRef = useRef(null);
  const running = sim?.status === 'running';

  const loadRecent = useCallback(() => axiosInstance.get(API).then(({ data }) => setRecent(data.simulations || [])).catch(() => {}), []);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => axiosInstance.get(`${API}${sim.id}/`).then(({ data }) => {
      setSim(data);
      if (data.status !== 'running') loadRecent();
    }).catch(() => {}), 1500);
    return () => clearInterval(t);
  }, [running, sim?.id, loadRecent]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [sim?.turns?.length, sim?.progress?.length, sim?.status]);

  const send = () => {
    const message = text.trim();
    if (!message || busy || running) return;
    setBusy(true); setError('');
    const req = sim ? axiosInstance.post(`${API}${sim.id}/`, { message }) : axiosInstance.post(API, { message, ...persona });
    req.then(({ data }) => { setSim(data); setText(''); setSince(Date.now()); })
      .catch((e) => setError(e?.response?.data?.error || 'Could not start the simulation.'))
      .finally(() => setBusy(false));
  };

  const open = (id) => axiosInstance.get(`${API}${id}/`).then(({ data }) => { setSim(data); setSince(Date.now()); });
  const fresh = () => { setSim(null); setText(''); setError(''); };
  const roll = () => {
    const p = PERSONAS[Math.floor(Math.random() * PERSONAS.length)];
    const s = SCENARIOS[Math.floor(Math.random() * SCENARIOS.length)];
    setPersona((x) => ({ ...x, name: p.name, language: p.language })); setText(s.text);
  };

  const who = sim ? (sim.customer?.name || 'Customer') : (persona.name || 'Customer');
  const turns = sim?.turns || [];
  const lastIndex = turns.length - 1;
  const empty = !sim;

  const placeholder = useMemo(() => (sim ? `Reply as ${who.split(' ')[0]}…` : `Write as ${who.split(' ')[0] || 'the customer'}, e.g. "Where is my order?"`), [sim, who]);

  return (
    <V2Shell>
      <div className="pg">
        <header className="pg-hero">
          <div className="flex items-center gap-3">
            <div className="pg-hero-mark"><ReplaiMark size={30} /></div>
            <div>
              <h1 className="text-[1.9rem] font-semibold leading-tight">Playground</h1>
              <p className="text-[0.98rem] opacity-90">Play the customer. See how Replai would answer, step by step. Nothing is ever sent.</p>
            </div>
          </div>
          {sim && <button type="button" onClick={fresh} className="pg-hero-btn"><Wand2 size={15} /> New conversation</button>}
        </header>

        <div className="pg-grid">
          <aside className="space-y-4">
            <section className="pg-card">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-semibold"><UserRound size={16} /> The customer</h2>
                <button type="button" onClick={roll} disabled={!!sim} className="pg-chip" title="Random customer and question"><Dice5 size={14} /> Surprise me</button>
              </div>
              <div className="mt-3 space-y-2">
                <label className="block text-sm"><span className="pg-label">Name</span>
                  <input className="pg-input w-full" value={sim ? sim.customer?.name || '' : persona.name} disabled={!!sim}
                    onChange={(e) => setPersona({ ...persona, name: e.target.value })} /></label>
                <label className="block text-sm"><span className="pg-label">Order number (optional, uses the real order)</span>
                  <input className="pg-input w-full" placeholder="#188662" value={sim ? '' : persona.order_number} disabled={!!sim}
                    onChange={(e) => setPersona({ ...persona, order_number: e.target.value })} /></label>
                <label className="block text-sm"><span className="pg-label">Email (optional, finds their orders)</span>
                  <input className="pg-input w-full" placeholder="customer@example.com" value={sim ? sim.customer?.email || '' : persona.email} disabled={!!sim}
                    onChange={(e) => setPersona({ ...persona, email: e.target.value })} /></label>
                <label className="block text-sm"><span className="pg-label">Customer writes in</span>
                  <select className="pg-input w-full" value={sim ? sim.customer?.language || '' : persona.language} disabled={!!sim}
                    onChange={(e) => setPersona({ ...persona, language: e.target.value })}>
                    {LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select></label>
              </div>
              {sim && <p className="mt-2 text-xs text-[var(--ink-3)]">Locked for this conversation. Start a new one to change it.</p>}
            </section>

            <section className="pg-card">
              <h2 className="flex items-center gap-2 font-semibold"><MessageCircleQuestion size={16} /> Earlier conversations</h2>
              {recent.length ? (
                <ul className="mt-2 space-y-1">
                  {recent.slice(0, 8).map((r) => (
                    <li key={r.id}>
                      <button type="button" onClick={() => open(r.id)}
                        className={`w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-[var(--paper)] ${sim?.id === r.id ? 'bg-[var(--pg-tint)]' : ''}`}>
                        <div className="truncate font-medium">{r.first_message || 'Simulation'}</div>
                        <div className="text-xs text-[var(--ink-3)]">{r.customer?.name || 'Customer'} · {r.turns_count} {r.turns_count === 1 ? 'message' : 'messages'} · {r.created_by} · {timeAgo(r.updated_at)}</div>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : <p className="mt-2 text-sm text-[var(--ink-3)]">None yet. Be the first!</p>}
            </section>
          </aside>

          <section className="pg-chat">
            <div className="pg-thread">
              {empty && (
                <div className="pg-empty">
                  <div className="pg-empty-mark"><Bot size={30} /></div>
                  <h2 className="text-xl font-semibold">Hi! I'm Replai.</h2>
                  <p className="mt-1 max-w-md text-[var(--ink-2)]">Pretend you're a customer and ask me anything. Pick a scenario or write your own, and watch me look things up before I answer.</p>
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    {SCENARIOS.map((s) => (
                      <button key={s.label} type="button" onClick={() => setText(s.text)} className="pg-scenario">
                        <span aria-hidden>{s.emoji}</span> {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {turns.map((t, i) => (
                <React.Fragment key={i}>
                  <div className="pg-row pg-row-customer">
                    <div className="pg-bubble">{t.customer}</div>
                    <Avatar who="customer" name={who} />
                  </div>
                  {(t.result || t.error || i < lastIndex || !running) && (
                    <div className="pg-row">
                      <Avatar who="replai" />
                      {t.result ? <Answer turn={t} sim={sim} index={i} onSaved={setSim} />
                        : <div className="pg-card text-red-800"><X size={15} className="mr-1 inline" />{t.error || 'No draft could be made.'}</div>}
                    </div>
                  )}
                </React.Fragment>
              ))}
              {running && (
                <div className="pg-row">
                  <Avatar who="replai" />
                  <Thinking progress={sim.progress} since={since} />
                </div>
              )}
              <div ref={endRef} />
            </div>

            <div className="pg-composer">
              {error && <p className="mb-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>}
              <div className="flex items-end gap-2">
                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder={placeholder}
                  disabled={running} className="pg-input min-h-[3rem] flex-1 resize-y"
                  onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(); }} />
                <button type="button" onClick={send} disabled={!text.trim() || busy || running} className="pg-send" aria-label="Send">
                  <Send size={18} />
                </button>
              </div>
              <p className="mt-1.5 text-xs text-[var(--ink-3)]">⌘ + Enter to send · runs on the real Bible and order data · never posted to Gorgias</p>
            </div>
          </section>
        </div>
      </div>
    </V2Shell>
  );
};

export default SimulateV2;
